import { Hammer, MessageSquare, ClipboardCheck, HeartHandshake, ShieldCheck, BadgeCheck, FileCheck } from "lucide-react";
import SEO from "../components/SEO";
import PageHero from "../components/PageHero";
import StatBand from "../components/StatBand";
import CTABanner from "../components/CTABanner";
import { Reveal } from "../components/motion";
import { IMG, COMPANY } from "../lib/site";

const VALUES = [
  {
    icon: Hammer,
    title: "Craftsmanship",
    desc: "Directly employed tradesmen who take pride in the details — because our name is on every project.",
  },
  {
    icon: MessageSquare,
    title: "Clear Communication",
    desc: "One dedicated project manager, regular updates and honest advice from the first survey onward.",
  },
  {
    icon: ClipboardCheck,
    title: "Turnkey Delivery",
    desc: "Design, planning, build and handover under one roof — one quote, one team, zero runaround.",
  },
  {
    icon: HeartHandshake,
    title: "Respect for Your Home",
    desc: "Dust sheets, floor protection and tidy sites every day. Your home treated like our own.",
  },
];

const CREDENTIALS = [
  {
    icon: ShieldCheck,
    title: "12-Month Workmanship Warranty",
    desc: "Every project is covered in writing for a full year after handover.",
  },
  {
    icon: BadgeCheck,
    title: "Fully Insured",
    desc: "Comprehensive public liability and contractor all-risks cover on every site.",
  },
  {
    icon: FileCheck,
    title: "Building-Control Certified",
    desc: "All structural work inspected, certified and signed off before handover.",
  },
];

const About = () => (
  <>
    <SEO
      title="About Us | Esskay Homes — Home Improvement & Construction"
      description="Esskay Homes is an established home improvement and construction company serving London & the South East. Fully insured, 12-month warranty, end-to-end project management."
      path="/about"
    />

    <PageHero
      eyebrow="About Esskay Homes"
      title={["Built on trust,", <>finished with <em className="italic text-gold">pride</em>.</>]}
      sub={`${COMPANY.area} · Registered company no. ${COMPANY.reg}`}
      image={IMG.craft}
      crumbs={["About Us"]}
    />

    {/* STORY */}
    <section className="container-ek grid items-center gap-14 py-24 lg:grid-cols-2 lg:gap-24 lg:py-32" data-testid="about-story">
      <div>
        <Reveal>
          <p className="eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-gold" />
            Our Story
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
            An established team, a personal{" "}
            <em className="italic text-gold">standard</em>.
          </h2>
        </Reveal>
        <Reveal delay={0.18}>
          <p className="mt-8 text-base leading-relaxed text-mist md:text-lg">
            Esskay Homes was founded on a simple frustration: too many home
            improvement projects start with excitement and end with excuses. We
            set out to be the opposite — a construction partner that turns up,
            sticks to the programme and finishes to a standard we'd accept in
            our own homes.
          </p>
        </Reveal>
        <Reveal delay={0.26}>
          <p className="mt-6 text-base leading-relaxed text-mist md:text-lg">
            We've grown into a close-knit
            team of tradesmen, designers and project managers serving{" "}
            {COMPANY.area.toLowerCase()} — from a single bathroom in Farnborough
            to full refurbishments and new builds across Surrey, Berkshire and
            London. We employ our own people, we don't overbook, and we treat
            every property as if the owner were watching.
          </p>
        </Reveal>
      </div>
      <div className="relative">
        <Reveal delay={0.15}>
          <div className="corner-frame relative overflow-hidden">
            <img
              src={IMG.craft2}
              alt="Esskay Homes craftsman on site"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
            />
          </div>
        </Reveal>
        <Reveal delay={0.3} className="absolute -bottom-10 -right-4 hidden w-56 md:block lg:-right-14">
          <div className="border-4 border-navy shadow-2xl">
            <img
              src={IMG.plans}
              alt="Design and planning drawings"
              className="aspect-[4/3] w-full object-cover"
              loading="lazy"
            />
          </div>
        </Reveal>
      </div>
    </section>

    <StatBand />

    {/* VALUES */}
    <section className="bg-navy2/40 py-24 lg:py-32" data-testid="about-values">
      <div className="container-ek">
        <div className="mb-14">
          <Reveal>
            <p className="eyebrow flex items-center gap-3">
              <span className="inline-block h-px w-10 bg-gold" />
              What We Stand For
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
              Four values, on every site, <em className="italic text-gold">every day</em>.
            </h2>
          </Reveal>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={0.08 * i} className="h-full">
              <div
                data-testid={`value-card-${i}`}
                className="group h-full border border-white/10 bg-navy3/50 p-8 transition-all duration-500 hover:border-gold/40"
              >
                <v.icon className="h-7 w-7 text-gold transition-transform duration-500 group-hover:-translate-y-1" />
                <h3 className="mt-6 font-serif text-2xl font-medium text-white">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">{v.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    {/* CREDENTIALS + TEAM */}
    <section className="container-ek grid gap-14 py-24 lg:grid-cols-[1.2fr_1fr] lg:gap-24 lg:py-32" data-testid="about-credentials">
      <div>
        <Reveal>
          <p className="eyebrow flex items-center gap-3">
            <span className="inline-block h-px w-10 bg-gold" />
            Insurances & Accreditations
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif text-4xl font-medium leading-[1.08] text-white sm:text-5xl">
            Protected, certified, <em className="italic text-gold">guaranteed</em>.
          </h2>
        </Reveal>
        <div className="mt-10 space-y-4">
          {CREDENTIALS.map((c, i) => (
            <Reveal key={c.title} delay={0.15 + i * 0.08}>
              <div
                data-testid={`credential-${i}`}
                className="flex items-start gap-5 border border-white/10 bg-navy2/50 p-6 transition-colors duration-500 hover:border-gold/30"
              >
                <c.icon className="mt-1 h-6 w-6 shrink-0 text-gold" />
                <div>
                  <h3 className="font-serif text-xl font-medium text-white md:text-2xl">{c.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-mist">{c.desc}</p>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={0.4}>
            <p className="pt-2 text-[11px] uppercase tracking-[0.2em] text-slate-600">
              {COMPANY.legalName} · Registered in England & Wales · Company No. {COMPANY.reg}
            </p>
          </Reveal>
        </div>
      </div>

      <div>
        <Reveal delay={0.2}>
          <div className="corner-frame relative overflow-hidden">
            <img
              src={IMG.living1}
              alt="Finished open-plan living space by Esskay Homes"
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
            />
          </div>
        </Reveal>
        <Reveal delay={0.3}>
          <div className="mt-8 border-l-2 border-gold pl-6">
            <p className="font-serif text-2xl font-medium leading-snug text-white md:text-3xl">
              “A close-knit team of tradesmen, designers and project managers —
              with decades of combined experience.”
            </p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-gold">
              The Esskay Team · London & the South East
            </p>
          </div>
        </Reveal>
      </div>
    </section>

    <CTABanner
      title={["Let's build", "something worth keeping."]}
      sub="Tell us about your home — we'll arrange a free survey and a detailed quote within days."
    />
  </>
);

export default About;
